import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Tooltip } from '@material-ui/core';
import { deepPure } from '~/util';
import { useClippingDateRangeUtil } from '~/modules/resourcing/common/hooks';
import { getStatusBasedClassName } from '~/modules/resourcing/common/enums';
import { getDynamicPositionAttributes } from '~/modules/common/charts/timeline/calculations';
import PeriodTimelineBlocks from '~/modules/resourcing/common/chart/components/PeriodTimelineBlocks';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import useRequestTimelineBlocks from '~/modules/resourcing/common/chart/hooks/useRequestTimelineBlocks';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import useResourceRequestDragIndicators from '../hooks/useResourceRequestDragIndicators';
import ResourceRequestTooltipContent from './ResourceRequestTooltipContent/ResourceRequestTooltipContent';
import useStyles from './useStyles';

export const ResourceRequestReadonlyBlock = ({
  scale,
  rowIndex,
  chartStartDate,
  resourceRequest,
  currencySymbol,
  classes: classesOverride,
  canViewCostData,
  chartDisplayDateRange,
  isAllocationBlockPresent = false,
  displayUnit,
  isPSAActualCornerCasesEnabled,
  isResourceActualModeEnabled
}) => {
  const classes = useStyles({ classes: classesOverride });

  const {
    isInChartRange,
    entityDateRange: requestDateRange,
    clippedChartDisplayDateRange
  } = useClippingDateRangeUtil({
    chartDisplayDateRange,
    entity: resourceRequest
  });

  const isStartChartBarClipped =
    requestDateRange.startDate < chartDisplayDateRange.startDate;
  const isEndChartBarClipped =
    requestDateRange.endDate > chartDisplayDateRange.endDate;

  const title = useMemo(
    () => (
      <ResourceRequestTooltipContent
        canViewCostData={canViewCostData}
        currencySymbol={currencySymbol}
        resourceRequest={resourceRequest}
      />
    ),
    [currencySymbol, resourceRequest, canViewCostData]
  );

  const { dynamicPosition } = useMemo(
    () =>
      getDynamicPositionAttributes({
        chartStartDate,
        scale,
        rowIndex,
        ...clippedChartDisplayDateRange
      }),
    [clippedChartDisplayDateRange, scale, chartStartDate]
  );

  const { project } = useProjectContext();
  const dragState = useResourceRequestDragIndicators({
    chartStartDate,
    scale,
    resourceRequest,
    project,
    isResourceActualModeEnabled,
    isPSAActualCornerCasesEnabled
  });

  const { requestPeriods } = useRequestTimelineBlocks({
    scheduleRules: resourceRequest.scheduleRules,
    chartStartDate,
    scale,
    startDate: mapIsoStringtoUtcObject(resourceRequest.startDate),
    endDate: mapIsoStringtoUtcObject(resourceRequest.endDate),
    relativeDynamicPositionLeft: dynamicPosition.left
  });

  return (
    isInChartRange && (
      <div
        className={classNames(classes.root, {
          [classes.disablePointerEvents]: isAllocationBlockPresent
        })}
        style={{
          ...dynamicPosition,
          marginTop: rowIndex > 0 ? `${rowIndex * 2}px` : `0px`
        }}
      >
        <Tooltip title={title}>
          <div
            className={classNames(
              classes.container,
              classes[
                getStatusBasedClassName(
                  resourceRequest.requestStatus,
                  'container'
                )
              ],
              {
                [classes.containerNoStart]: isStartChartBarClipped,
                [classes.containerNoEnd]: isEndChartBarClipped
              }
            )}
          >
            {!isAllocationBlockPresent && (
              <PeriodTimelineBlocks
                quantity={resourceRequest.quantity}
                scale={scale}
                displayUnit={displayUnit}
                displayPeriods={requestPeriods}
                gestureBindEvents={dragState.gestureBindEvents}
                dragDelta={dragState.dragDelta}
                resizeDirection={dragState.currentResizeDirection}
              />
            )}
          </div>
        </Tooltip>
      </div>
    )
  );
};

ResourceRequestReadonlyBlock.propTypes = {
  scale: PropTypes.string.isRequired,
  resourceRequest: PropTypes.object.isRequired,
  currencySymbol: PropTypes.string,
  chartDisplayDateRange: PropTypes.object.isRequired,
  chartStartDate: PropTypes.object.isRequired,
  canViewCostData: PropTypes.bool,
  isAllocationBlockPresent: PropTypes.bool,
  displayUnit: PropTypes.string,
  rowIndex: PropTypes.number,
  classes: PropTypes.object,
  isPSAActualCornerCasesEnabled: PropTypes.bool,
  isResourceActualModeEnabled: PropTypes.bool
};

export default deepPure(ResourceRequestReadonlyBlock);
